import React, { useState, useEffect } from "react";
import { Flex, Heading, Text, Button, Icon } from "@chakra-ui/react"; // Add these imports
import { useTable, useSortBy } from "react-table";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import {
  textLightBlue,
  primary,
  white,
  textPrimary,
  newBorder,
  textSecondary,
  primaryAlt,
} from "../../../utils/colors"; // Assuming colors
import noServices from "../../../assets/images/empty-box.png";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import useThemeColors from "../../../hooks/useThemeColors";
import NoItemsDefaultNew from "./partials/NoItemsDefaultNew";

const TableContainer = ({
  loading,
  error,
  columns,
  data,
  className,
  noItemsTitle,
  noItemsDescription,
  noItemsButtonHandler,
  noItemsButtonLabel,
  noItemsButtonDisabled,
  onRowClick,
  sortField,
  sortOrder,
  isHeaderWhite,
  noItemsAlt,
  newTableItemId,
}) => {
  const { t } = useTranslation();
  const { whiteMode, bgLightMode, hoverItemColorMode } = useThemeColors();

  // STATE
  const [sortState, setSortState] = useState({ sortField, sortOrder });

  // Update to generate sorting indicators manually based on sortState
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useSortBy
    );

  // Update to generate sorting indicators manually based on sortState
  const generateSortingIndicator = (columnId) => {
    return sortState.sortField === columnId ? (
      sortState.sortOrder === "desc" ? (
        <TriangleDownIcon aria-label="sorted descending" />
      ) : (
        <TriangleUpIcon aria-label="sorted ascending" />
      )
    ) : null;
  };

  // Example function to handle sort change (if you want to implement sorting changes via UI)
  const handleSortChange = (columnId) => {
    const isAsc =
      sortState.sortField === columnId && sortState.sortOrder === "asc";
    const newSortOrder = isAsc ? "desc" : "asc";
    setSortState({ sortField: columnId, sortOrder: newSortOrder });
    // Here you would trigger the data fetch based on new sort criteria
  };

  // You can add a new condition here to handle the case when the data length is zero
  if (!data || (data.length === 0 && !loading)) {
    return (
      <>
        {
          <NoItemsDefaultNew
            noItemsButtonDisabled={noItemsButtonDisabled}
            noItemsButtonHandler={noItemsButtonHandler}
            noItemsButtonLabel={noItemsButtonLabel}
            noItemsDescription={noItemsDescription}
            noItemsTitle={noItemsTitle}
          />
        }
      </>
    );
  }

  // Example function to handle row click
  const handleRowClick = (e, rowId) => {
    // Exclude clicks on input elements like switches or buttons
    if (!["INPUT", "BUTTON"].includes(e.target.tagName)) {
      onRowClick(rowId);
    }
  };

  // LOGS

  // console.log("loading", loading);
  // console.log("error", error);
  // console.log("data", data);
  // console.log("columns", columns);

  return (
    <>
      <Flex w="100%" overflowX={["scroll", "hidden", "hidden"]}>
        <Table {...getTableProps()} className={className} w="100%">
          <Thead>
            {headerGroups.map((headerGroup) => {
              // Extract the key and rest of the props
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              const { key: headerGroupKey, ...restHeaderGroupProps } =
                headerGroupProps;

              return (
                <Tr key={headerGroupKey} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    // Extract the key and rest of the props
                    const columnProps = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    const { key: columnKey, ...restColumnProps } = columnProps;

                    return (
                      <Th
                        key={columnKey}
                        {...restColumnProps}
                        bg={isHeaderWhite ? whiteMode : bgLightMode}
                        color={textLightBlue}
                        h={isHeaderWhite ? "2.5rem" : "3rem"}
                        fontSize="0.688rem"
                        fontWeight="600"
                        borderBottom={
                          isHeaderWhite ? `1px solid ${newBorder}` : "none"
                        }
                        p="0 1.5rem"
                        onClick={() => handleSortChange(column.id)}
                      >
                        <Flex align="center" h="100%">
                          {column.render("Header")}
                          {generateSortingIndicator(column.id)}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              );
            })}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              // Extract the key and rest of the props
              const rowProps = row.getRowProps();
              const { key: rowKey, ...restRowProps } = rowProps;

              return (
                <Tr
                  key={rowKey}
                  {...restRowProps}
                  onClick={(e) => handleRowClick(e, row.original._id)}
                  bg={row.original._id === newTableItemId ? "#ffffce" : "none"}
                  _hover={{ bg: hoverItemColorMode, cursor: "pointer" }}
                >
                  {row.cells.map((cell) => {
                    // Extract the key and rest of the props
                    const cellProps = cell.getCellProps();
                    const { key: cellKey, ...restCellProps } = cellProps;

                    return (
                      <Td key={cellKey} {...restCellProps}>
                        {cell.render("Cell")}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Flex>
    </>
  );
};

export default TableContainer;
